import React, { useEffect, useState } from 'react';
import { IOrder, ISearchOrderReq, ISearchOrderRes } from '@/interfaces/order';
import { DatePicker, Input, TablePaginationConfig } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { searchOrders } from '@/adapters/order';
import { FilterValue, SorterResult } from 'antd/es/table/interface';
import locale from 'antd/es/date-picker/locale/th_TH';
import { Dayjs } from 'dayjs';

const { Search } = Input;

const _criteria: ISearchOrderReq = {
  orderBy: 'id',
  orderType: 'descend',
  pageSize: 10,
  pageStart: 1,
  search: '',
  include: true,
};

import CustomerOrderTable from './components/customer-order-table';
import { ICustomer } from '@/interfaces/customer';

interface IProps {
  data: ICustomer;
}

const CustomerOrder = ({ data: customer }: IProps) => {
  const [criteria, setCriteria] = useState<ISearchOrderReq>({
    ..._criteria,
    customerId: customer?.id,
  });
  const [order, setOrder] = useState<ISearchOrderRes>({
    data: [],
    totalRecords: 0,
  });

  const mutations = useMutation({
    mutationFn: async () => await searchOrders(criteria),
    mutationKey: ['fetchOrder'],
    onSuccess: data => {
      if (data?.status != 200) {
        throw new Error();
      } else {
        setOrder(data?.data);
      }
    },
    onError: error => {
      console.log(error);
    },
  });

  const fetchOrder = async () => {
    try {
      mutations.mutateAsync();
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (criteria) {
      fetchOrder();
    }
  }, [criteria]);

  const onTableChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<IOrder> | SorterResult<IOrder>[],
  ) => {
    const newCriteria = { ...criteria };
    if (!Array.isArray(sorter)) {
      newCriteria.orderBy = (sorter.column?.dataIndex as keyof IOrder) || '';
      newCriteria.orderType = sorter.order || '';
    }
    setCriteria({
      ...newCriteria,
      pageStart: Number(pagination.current || 1),
      pageSize: Number(pagination.pageSize || 10),
    });
  };

  const onSearch = (text: string) => {
    setCriteria({
      ...criteria,
      search: text,
      orderBy: 'id',
      orderType: '',
      pageStart: 1,
      pageSize: 10,
    });
  };

  const onRangeChange = (dates: null | (Dayjs | null)[]) => {
    if (dates) {
      setCriteria({
        ...criteria,
        orderBy: 'id',
        orderType: '',
        pageStart: 1,
        pageSize: 10,
        startDate: dates[0]?.toDate(),
        endDate: dates[1]?.toDate(),
      });
    } else {
      setCriteria({
        ...criteria,
        orderBy: 'id',
        orderType: '',
        pageStart: 1,
        pageSize: 10,
        startDate: null,
        endDate: null,
      });
    }
  };
  return (
    <>
      <div className="flex flex-col gap-3">
        <div className="flex flex-col md:flex-row justify-between gap-3">
          <div className="flex flex-col md:flex-row justify-between gap-3">
            <DatePicker.RangePicker
              size="large"
              locale={locale}
              onChange={onRangeChange}
            />
            <Search
              className="md:max-w-[400px] md:min-w-[400px]"
              placeholder="ค้นหา"
              allowClear
              size="large"
              enterButton="ค้นหา"
              onSearch={onSearch}
            />
          </div>
        </div>
        <CustomerOrderTable
          criteria={criteria}
          order={order}
          loading={mutations.isPending}
          onEditData={item => console.log(item)}
          onTableChange={onTableChange}
        />
      </div>
    </>
  );
};

export default CustomerOrder;
